// src/features/messages/components/ComposeMessageDialog.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { messagesAPI } from '../services/messages';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import AudienceSelector, { recipientIds } from './AudienceSelector';
import { MAX_CONTENT_LENGTH } from '../constants';

// The character count only matters close to the limit
const SHOW_COUNT_FROM = MAX_CONTENT_LENGTH - 500;

const EMPTY_AUDIENCE = { all: true, roleIds: [], staffIds: [] };

// Roomier than the default dialog padding (theme spacing is 4px per unit)
const PAD_X = { xs: 6, sm: 10 };

export default function ComposeMessageDialog({ open, onClose, currentUserId }) {
  const { t } = useTranslation(['messages', 'common']);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const [content, setContent] = useState('');
  const [audience, setAudience] = useState(EMPTY_AUDIENCE);
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContent('');
    setAudience(EMPTY_AUDIENCE);
    setOptionsError(null);
    messagesAPI.audienceOptions()
      .then(({ data }) => setOptions(data))
      .catch((err) => setOptionsError(err?.response?.data?.message || t('messages:feedback.load_error')));
  }, [open, t]);

  const recipients = useMemo(
    () => recipientIds(audience, options, currentUserId).size,
    [audience, options, currentUserId],
  );
  const canSend = content.trim().length > 0 && recipients > 0 && !sending;

  // Esc, a click outside or Cancel would otherwise throw away what was written
  const requestClose = () => {
    if (sending) return;
    if (!content.trim()) {
      onClose();
      return;
    }
    showConfirmDialog({
      title: t('messages:compose.discard_title'),
      message: t('messages:compose.discard_text'),
      confirmText: t('messages:compose.discard'),
      severity: 'error',
      onConfirm: onClose,
    });
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await messagesAPI.send({
        content: content.trim(),
        audience_all: audience.all,
        role_ids: audience.all ? [] : audience.roleIds,
        staff_member_ids: audience.all ? [] : audience.staffIds,
      });
      showSuccess(t('messages:feedback.sent', { count: recipients }));
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || t('messages:feedback.send_error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={requestClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ px: PAD_X, pt: 7 }}>{t('messages:compose.title')}</DialogTitle>
      <DialogContent dividers sx={{ px: PAD_X, py: 7 }}>
        <Stack spacing={7}>
          <TextField
            value={content}
            onChange={(e) => setContent(e.target.value)}
            label={t('messages:compose.content_label')}
            placeholder={t('messages:compose.content_placeholder')}
            multiline
            minRows={4}
            maxRows={12}
            fullWidth
            autoFocus
            disabled={sending}
            helperText={content.length >= SHOW_COUNT_FROM
              ? t('messages:compose.character_count', { count: content.length, max: MAX_CONTENT_LENGTH })
              : undefined}
            slotProps={{ htmlInput: { maxLength: MAX_CONTENT_LENGTH } }}
          />

          {optionsError ? (
            <Alert severity="error">{optionsError}</Alert>
          ) : options ? (
            <AudienceSelector
              value={audience}
              onChange={setAudience}
              options={options}
              currentUserId={currentUserId}
              disabled={sending}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: PAD_X, py: 5 }}>
        <Button onClick={requestClose} disabled={sending}>{t('common:buttons.cancel')}</Button>
        {/* Says how many it goes to, right where the decision is made */}
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        >
          {sending
            ? t('messages:compose.sending')
            : recipients > 0 ? t('messages:compose.send_to', { count: recipients }) : t('messages:compose.send')}
        </Button>
      </DialogActions>
      <ConfirmDialog />
    </Dialog>
  );
}
